import { Injectable, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { from, Observable, Subject } from 'rxjs';

import { LocalStorageService } from '@recruitee/common';
import { OfferKind, TOfferKind } from '@recruitee/http/types';
import { DialogConfigOptions } from '@recruitee/user-interface';

import { AppNavigatorRoot, AppNavigatorRoute } from './navigation.types';

@Injectable({ providedIn: 'root' })
export class AppNavigatorService {
  public readonly NAVIGATOR_HISTORY_CACHE_KEY: string = 'navigator_history_2';

  // TODO use routes instead
  public interviewScheduleDialogOpen$ = new Subject<{
    data: any;
    viewContainerRef?: any;
  }>();

  // TODO use routes instead
  public interviewEventDialogOpen$ = new Subject<{
    eventId: number;
    inCandidate?: boolean;
    config?: DialogConfigOptions;
    viewContainerRef?: any;
  }>();

  private navigatorHistory: Map<AppNavigatorRoot, AppNavigatorRoute> = new Map([
    [AppNavigatorRoot.Dashboard, { url: '/dashboard/overview' }],
    [AppNavigatorRoot.Mailbox, { url: '/mailbox/my/all' }],
  ]);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private localStorage: LocalStorageService,
  ) {
    this.getNavigatorHistoryFromCache();
  }

  public getLastRoute(root: AppNavigatorRoot): AppNavigatorRoute | undefined {
    return this.navigatorHistory.has(root) ? this.navigatorHistory.get(root) : undefined;
  }

  public saveLastRoute(
    root: AppNavigatorRoot,
    route: AppNavigatorRoute,
    saveToCache: boolean = true,
  ): void {
    if (!route.url || route.url.indexOf(`/${root}/`) !== 0 || route.url === `/${root}/`) {
      throw new TypeError(`Invalid route.url: "${route.url}"`);
    }

    this.navigatorHistory.set(root, route);
    if (saveToCache) {
      this.saveNavigatorHistoryToCache();
    }
  }

  public navigateToLastRoute(root: AppNavigatorRoot): Promise<boolean> {
    const { url, extras } = this.getLastRoute(root)!;
    return this.router.navigate([url], extras);
  }

  public openCandidateDialog(
    candidateId: number,
    data: {
      tab?: string;
      messagesMode?: string;
      smsThread?: string;
      openInterview?: boolean;
      interviewOfferId?: number;
      interviewTemplateId?: number;
      interviewResultRequestId?: number;
      interviewEdit?: boolean;
      interviewResultId?: number;
      fromHiddenEvaluations?: boolean;
    } = {},
  ): Observable<boolean> {
    const currentCandidateId = this.route.snapshot.queryParams.candidate
      ? +this.route.snapshot.queryParams.candidate
      : undefined;
    const open = this.router.navigate([], {
      queryParams: {
        candidate: candidateId,
        tab: data.tab,
        messagesMode: data.messagesMode,
        smsThread: data.smsThread,
        openInterview: data.openInterview,
        interviewOfferId: data.interviewOfferId,
        interviewTemplateId: data.interviewTemplateId,
        interviewResultRequestId: data.interviewResultRequestId,
        interviewEdit: data.interviewEdit,
        interviewResultId: data.interviewResultId,
        fromHiddenEvaluations: data.fromHiddenEvaluations,
      },
      queryParamsHandling: 'merge',
      replaceUrl: currentCandidateId === candidateId,
    });

    return from(open);
  }

  public navigateToOfferPage(offer: { kind: TOfferKind; slug: string }): void {
    let url = '';
    if (offer.kind === OfferKind.Job) {
      url = `/offers/${offer.slug}/pipeline`;
    } else {
      url = `/talent_pools/${offer.slug}/filters`;
    }
    this.router.navigate([url]);
  }

  public openInterviewScheduleDialog(event: {
    data: any;
    viewContainerRef?: ViewContainerRef;
  }): void {
    this.interviewScheduleDialogOpen$.next(event);
  }

  public openInterviewEventDialog(
    eventId: number,
    {
      inCandidate,
      config,
      viewContainerRef,
    }: { inCandidate?: boolean; config?: DialogConfigOptions; viewContainerRef?: ViewContainerRef },
  ): void {
    this.interviewEventDialogOpen$.next({ eventId, inCandidate, config, viewContainerRef });
  }

  private getNavigatorHistoryFromCache(): void {
    const serializedHistory = this.localStorage.get(this.NAVIGATOR_HISTORY_CACHE_KEY, {});
    Object.entries(serializedHistory).forEach(([key, value]: [string, { url: string }]) => {
      // Support for cache before changes on 27th Mar 2018
      if (
        value &&
        value.url &&
        'dashboard /dashboard /dashboard/ mailbox /mailbox /mailbox/'.split(' ').includes(value.url)
      ) {
        return;
      } else if (value && value.url && value.url[0] !== '/') {
        // eslint-disable-next-line no-param-reassign
        value = { ...value, url: `/${value.url}` };
      }

      this.navigatorHistory.set(<AppNavigatorRoot>key, value);
    });
  }

  private saveNavigatorHistoryToCache(): void {
    const serializedHistory = Array.from(this.navigatorHistory.entries()).reduce(
      (agg, redirectPair) => ({ ...agg, [redirectPair[0]]: redirectPair[1] }),
      {},
    );
    this.localStorage.set(this.NAVIGATOR_HISTORY_CACHE_KEY, serializedHistory);
  }
}
